import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "antd";
import { RouteJob, RouteJobDetails } from "../routingApi";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from '@dnd-kit/utilities';
import { faGripDotsVertical } from "@fortawesome/pro-duotone-svg-icons";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons";

export type SortableJobProps = {
  routeJob: RouteJob;
  job: RouteJobDetails;
  isSold?: boolean;
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  disabled?: boolean;
  visibleIndex?: number;
  onClick?: () => void;
}

const SortableJob = ({ routeJob, job, isSold = true, isHovered, onMouseEnter, onMouseLeave, disabled, visibleIndex, onClick }: SortableJobProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: routeJob.sortIndex, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 150ms ease',
    border: isHovered ? '1px solid #FFCABD' : '1px solid transparent',
    borderRadius: '12px',
    padding: '12px 16px',
    marginBottom: '12px',
    background: '#fff',
    display: 'flex',
    flexDirection: 'column' as const,
    boxShadow: isHovered ? '0 0 0 1px #FFCABD, 0 1px 2px rgba(0,0,0,0.03)' : '0 1px 2px rgba(0,0,0,0.03)',
    outline: undefined,
    outlineOffset: undefined,
    opacity: disabled ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div 
          id={visibleIndex === 0 ? 'drag-handle' : undefined}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginRight: 8,
            cursor: disabled ? 'not-allowed' : 'grab',
            color: disabled ? '#ccc' : '#888',
            padding: '4px 8px',
            borderRadius: '4px',
          }}
          {...attributes}
          {...listeners}
        >
          <FontAwesomeIcon icon={faGripDotsVertical} style={{ fontSize: '16px' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minWidth: 24, marginRight: 8 }}>
          <span style={{ fontWeight: 600, color: disabled ? '#ccc' : '#888', fontSize: 13 }}>
            {disabled ? '-' : (visibleIndex !== undefined ? visibleIndex + 1 : routeJob.sortIndex + 1)}
          </span>
        </div>
        <div 
          id={visibleIndex === 0 ? 'job-name' : undefined}
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            flex: 1,
            cursor: onClick ? 'pointer' : 'default'
          }}
          onClick={onClick}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: disabled ? '#999' : 'inherit' }}>{job.jobName}</div>
            {isSold === false && (
              <Tooltip title="This job isn't sold, so it no longer appears in the job list. It will keep generating scheduled events while it stays on the route." placement="top">
                <FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#faad14', fontSize: 13 }} />
              </Tooltip>
            )}
          </div>
          <div style={{ fontSize: 13, color: disabled ? '#ccc' : '#888', marginTop: 2 }}>{job.customerDisplayName}</div>
        </div>
      </div>
    </div>
  );
};

export default SortableJob